import { AlertTriangle, ShieldCheck } from 'lucide-react';
import { getPoisonHotline } from '../../../constants/disclaimers';

/**
 * ToxicWarningBadge - Displays toxicity information with appropriate styling
 *
 * Shows either a warning badge (red) for toxic plants or a safe badge (green)
 * for non-toxic plants. Provides detailed toxicity information.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isToxic - Whether plant is toxic
 * @param {string} props.toxicityLevel - Toxicity level (none, low, medium, high, severe)
 * @param {boolean} props.toxicToPets - Toxic to pets in general
 * @param {boolean} props.toxicToChildren - Toxic to children
 * @param {boolean} props.toxicToCats - Toxic to cats
 * @param {boolean} props.toxicToDogs - Toxic to dogs
 * @param {string|null} [props.country=null] - Country name from GardenSettingsContext. Falls back to DE.
 */
const ToxicWarningBadge = ({
  isToxic,
  toxicityLevel = 'none',
  toxicToPets,
  toxicToChildren,
  toxicToCats,
  toxicToDogs,
  country = null,
}) => {
  const hotline = getPoisonHotline(country);

  // Toxicity level descriptions
  const toxicityLevelDescriptions = {
    'none': 'Diese Pflanze ist ungiftig und sicher für Kinder, Haustiere und Erwachsene.',
    'low': 'Geringe Giftigkeit. Kann bei Hautkontakt oder geringfügiger Einnahme zu leichten Reizungen führen. Symptome: Leichter Hautausschlag, Magenverstimmung.',
    'medium': 'Mittlere Giftigkeit. Bei Einnahme können mäßige Symptome auftreten. Symptome: Übelkeit, Erbrechen, Durchfall. Bei Verzehr Giftnotruf kontaktieren.',
    'high': 'Hohe Giftigkeit. Medizinische Behandlung erforderlich. Symptome: Starkes Erbrechen, Atembeschwerden, Verwirrung. Sofort ärztliche Hilfe aufsuchen.',
    'severe': `Sehr hohe Giftigkeit. Lebensbedrohlich. Symptome: Herzprobleme, Krampfanfälle, Bewusstlosigkeit. Sofort 112 für den Rettungsdienst wählen und parallel den Giftnotruf ${hotline.label} kontaktieren.`,
  };

  const description = toxicityLevelDescriptions[toxicityLevel] || toxicityLevelDescriptions['none'];

  // Build warning details
  const warnings = [];
  if (toxicToChildren) warnings.push('Giftig für Kinder');
  if (toxicToPets) warnings.push('Giftig für Haustiere');
  if (toxicToCats && !toxicToPets) warnings.push('Giftig für Katzen');
  if (toxicToDogs && !toxicToPets) warnings.push('Giftig für Hunde');

  if (!isToxic) {
    // Safe Badge (Green)
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-bold text-green-900 mb-1">Ungiftige Pflanze</div>
            <p className="text-sm text-green-800 leading-relaxed">{description}</p>
          </div>
        </div>
      </div>
    );
  }

  // Toxic Warning Badge (Red)
  const getSeverityStyles = () => {
    switch (toxicityLevel) {
      case 'severe':
        return {
          bg: 'bg-red-100',
          border: 'border-red-300',
          icon: 'text-red-700',
          title: 'text-red-950',
          text: 'text-red-900'
        };
      case 'high':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          icon: 'text-red-600',
          title: 'text-red-900',
          text: 'text-red-800'
        };
      case 'medium':
        return {
          bg: 'bg-orange-50',
          border: 'border-orange-200',
          icon: 'text-orange-600',
          title: 'text-orange-900',
          text: 'text-orange-800'
        };
      case 'low':
      default:
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          icon: 'text-yellow-600',
          title: 'text-yellow-900',
          text: 'text-yellow-800'
        };
    }
  };

  const styles = getSeverityStyles();

  const getToxicityLevelLabel = () => {
    const labels = {
      'severe': 'Sehr hohe Giftigkeit',
      'high': 'Hohe Giftigkeit',
      'medium': 'Mittlere Giftigkeit',
      'low': 'Geringe Giftigkeit'
    };
    return labels[toxicityLevel] || 'Giftigkeit';
  };

  return (
    <div className={`${styles.bg} border ${styles.border} rounded-lg p-4`}>
      <div className="flex items-start gap-3">
        <AlertTriangle className={`w-5 h-5 ${styles.icon} flex-shrink-0 mt-0.5`} />
        <div className="flex-1">
          <div className={`text-sm font-bold ${styles.title} mb-1`}>
            {getToxicityLevelLabel()}
          </div>

          {/* Warning badges */}
          {warnings.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {warnings.map((warning, index) => (
                <span
                  key={index}
                  className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${styles.bg} ${styles.text} border ${styles.border}`}
                >
                  {warning}
                </span>
              ))}
            </div>
          )}

          {/* Description */}
          <p className={`text-sm ${styles.text} leading-relaxed`}>
            {description}
          </p>

          {/* Emergency notice for severe toxicity */}
          {toxicityLevel === 'severe' && (
            <div className="mt-3 p-2 bg-red-200 border border-red-400 rounded">
              <p className="text-xs font-bold text-red-950">
                Notfall: Bei Verzehr sofort Rettungsdienst{' '}
                <a
                  href="tel:112"
                  className="underline underline-offset-2 inline-flex items-center min-h-[44px] px-1"
                >
                  112
                </a>{' '}
                wählen und parallel Giftnotruf{' '}
                <a
                  href={`tel:${hotline.tel}`}
                  className="underline underline-offset-2 inline-flex items-center min-h-[44px] px-1"
                >
                  {hotline.label}
                </a>{' '}
                kontaktieren.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ToxicWarningBadge;
